/**
 * @file Whole-app search. Indexes the two big entity collections
 * (employees + devices) and substring-matches across their human-facing
 * fields. Internal tool scale (~765 employees, ~1200 devices), so we load
 * once per session into memory and filter client-side — no full-text
 * service needed.
 *
 * Test-mode-aware: it reads through db.js's `cols` helpers, so in test
 * mode it searches the per-user sandbox (and the cache is keyed by mode so
 * the two never mix).
 */

import { getDocs } from 'firebase/firestore';
import { cols } from './db.js';
import { isTestMode } from './test-mode.js';
import { phoneDigits, matchSimNumber, looksLikePhoneQuery } from './lib/phone-match.js';

let cache = null; // { mode, employees: [...], devices: [...] }

function modeKey() {
  return isTestMode() ? 'test' : 'prod';
}

async function loadIndex() {
  if (cache && cache.mode === modeKey()) return cache;
  const [empSnap, devSnap] = await Promise.all([
    getDocs(cols.employees()),
    getDocs(cols.devices()),
  ]);
  cache = {
    mode: modeKey(),
    employees: empSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
    devices:   devSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
  };
  return cache;
}

/** Drop the cached index (e.g. on sign-out). Mode changes self-invalidate. */
export function clearSearchIndex() {
  cache = null;
}

/**
 * Run a search. Returns `{ people, devices, query }`, each list capped.
 * Needs ≥2 chars; shorter queries return empty so the dropdown stays quiet
 * while typing the first letter.
 */
export async function runGlobalSearch(needle, { limit = 8 } = {}) {
  const q = String(needle || '').trim().toLowerCase();
  if (q.length < 2) return { people: [], devices: [], query: q };

  const idx = await loadIndex();

  // Phone numbers can't go in the text haystack: they're stored in every
  // format going (+420 / spaces / bare) inside the Bamboo SIM assets, so they
  // match on normalised digits instead. `matchedPhone` rides along on the
  // result so the UI can show WHICH number hit — which is what makes a number
  // assigned to two people (a Bamboo mistake) obvious at a glance.
  const qDigits = looksLikePhoneQuery(q) ? phoneDigits(q) : '';
  const people = [];
  for (const e of idx.employees) {
    const hay = `${e.fullName || ''} ${e.email || ''} ${e.homeEmail || ''} ${e.employeeNumber || ''} ${e.jobTitle || ''} ${e.department || ''}`.toLowerCase();
    if (hay.includes(q)) { people.push(e); continue; }
    const phone = qDigits ? matchSimNumber(e, qDigits) : null;
    if (phone) people.push({ ...e, matchedPhone: phone });
  }
  people.splice(limit);

  const deviceHay = (d) => `${d.name || ''} ${d.sn || ''} ${d.flexiKod || ''} ${d.category || ''} ${d.testing?.deviceTag || ''} ${d.testing?.imei || ''} ${d.currentHolderEmail || ''} ${d.lastHolderEmail || ''}`.toLowerCase();
  const deviceMatches = idx.devices.filter((d) => deviceHay(d).includes(q));

  // Decommissioned devices stay OUT of the result list (registry-only, own
  // tab), but they are COUNTED: a bare "no results" for a serial Gear
  // actually knows reads as "we never had this laptop" — the exact wrong
  // conclusion when someone is chasing a returned or MDM-flagged machine.
  // The caller renders the count as a hint pointing at the registry tab.
  const devices = deviceMatches
    .filter((d) => d.status !== 'decommissioned')
    .slice(0, limit);
  const decommissionedCount = deviceMatches.filter((d) => d.status === 'decommissioned').length;

  return { people, devices, decommissionedCount, query: q };
}
